import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Loader2, Forward, Send } from "lucide-react";
import {
  classifyWhatsAppForwardKind,
  whatsAppForwardKindLabel,
} from "@/lib/whatsapp-forward-message";

interface LeadOption {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
}

export interface ForwardMessageSource {
  id: string;
  content: string;
  metadata?: Record<string, unknown> | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  memberId?: string | null;
  sourceMessage: ForwardMessageSource | null;
  /** Integração da conversa onde a mensagem foi recebida/enviada — encaminha pelo mesmo canal. */
  sourceIntegrationId?: string | null;
  sourceIntegrationLabel?: string | null;
  currentLeadId?: string | null;
  onForwarded?: (result: {
    conversationId: string;
    leadId: string;
    leadName: string;
  }) => void;
}

export function ForwardMessageDialog({
  open,
  onOpenChange,
  workspaceId,
  memberId,
  sourceMessage,
  sourceIntegrationId,
  sourceIntegrationLabel,
  currentLeadId,
  onForwarded,
}: Props) {
  const { toast } = useToast();

  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<LeadOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedLead, setSelectedLead] = useState<LeadOption | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [sending, setSending] = useState(false);

  const forwardKind = sourceMessage
    ? classifyWhatsAppForwardKind(sourceMessage.metadata, sourceMessage.content)
    : null;

  useEffect(() => {
    if (!open) {
      setSearchTerm("");
      setResults([]);
      setSelectedLead(null);
      setSending(false);
    }
  }, [open]);

  const searchLeads = useCallback(
    async (term: string) => {
      if (!workspaceId || term.length < 2) {
        setResults([]);
        setSearching(false);
        return;
      }
      setSearching(true);
      try {
        const { data, error } = await supabase
          .from("leads")
          .select("id, name, phone, email")
          .eq("workspace_id", workspaceId)
          .or(`name.ilike.%${term}%,phone.ilike.%${term}%,email.ilike.%${term}%`)
          .order("name", { ascending: true })
          .limit(20);
        if (error) throw error;
        const rows = (data ?? []) as LeadOption[];
        setResults(rows.filter((l) => l.id !== currentLeadId));
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    },
    [workspaceId, currentLeadId],
  );

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void searchLeads(searchTerm.trim());
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [open, searchTerm, searchLeads]);

  const previewText =
    sourceMessage?.content?.trim().slice(0, 120) ||
    (forwardKind ? whatsAppForwardKindLabel(forwardKind) : "Mensagem");

  const handleForward = async () => {
    if (!sourceMessage?.id || !selectedLead) return;
    if (!selectedLead.phone) {
      toast({
        title: "Lead sem telefone",
        description: "O lead selecionado não possui telefone para WhatsApp.",
        variant: "destructive",
      });
      return;
    }
    if (!sourceIntegrationId) {
      toast({
        title: "Conexão indisponível",
        description: "Esta conversa não tem uma conexão WhatsApp vinculada para encaminhar.",
        variant: "destructive",
      });
      return;
    }

    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("forward-message", {
        body: {
          workspace_id: workspaceId,
          source_message_id: sourceMessage.id,
          target_lead_id: selectedLead.id,
          integration_id: sourceIntegrationId,
          member_id: memberId ?? undefined,
        },
      });

      if (error) {
        throw new Error(
          typeof error === "object" && error !== null && "message" in error
            ? String((error as { message?: string }).message)
            : "Não foi possível encaminhar.",
        );
      }

      if (data?.error) {
        throw new Error(String(data.error));
      }

      if (!data?.success) {
        toast({
          title: "Falha ao encaminhar",
          description: data?.friendly_message || "Não foi possível encaminhar a mensagem.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Mensagem encaminhada",
        description: `Enviada para ${data.target_lead_name || selectedLead.name}`,
      });

      onForwarded?.({
        conversationId: data.conversation_id,
        leadId: data.target_lead_id,
        leadName: data.target_lead_name || selectedLead.name,
      });
      onOpenChange(false);
    } catch (err) {
      toast({
        title: "Erro ao encaminhar",
        description: err instanceof Error ? err.message : "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Forward className="h-5 w-5" />
            Encaminhar mensagem
          </DialogTitle>
          <DialogDescription>
            {forwardKind
              ? `Encaminhar ${whatsAppForwardKindLabel(forwardKind).toLowerCase()} para outro lead, pela mesma conexão desta conversa.`
              : "Selecione o lead de destino."}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground line-clamp-3">
          {previewText}
        </div>

        {sourceIntegrationId ? (
          <p className="text-xs text-muted-foreground">
            Conexão:{" "}
            <span className="font-medium text-foreground">
              {sourceIntegrationLabel || "WhatsApp desta conversa"}
            </span>
          </p>
        ) : (
          <p className="text-sm text-destructive">
            Esta conversa não tem conexão WhatsApp vinculada. Não é possível encaminhar.
          </p>
        )}

        <div className="space-y-2">
          <Label>Lead de destino</Label>
          <Command shouldFilter={false} className="rounded-lg border">
            <CommandInput
              placeholder="Buscar lead por nome ou telefone…"
              value={searchTerm}
              onValueChange={setSearchTerm}
            />
            <CommandList>
              <CommandEmpty>
                {searching ? "Buscando…" : searchTerm.length < 2 ? "Digite ao menos 2 caracteres" : "Nenhum lead encontrado"}
              </CommandEmpty>
              <CommandGroup>
                {results.map((lead) => (
                  <CommandItem
                    key={lead.id}
                    value={lead.id}
                    onSelect={() => setSelectedLead(lead)}
                    className={selectedLead?.id === lead.id ? "bg-accent" : ""}
                  >
                    <div className="flex flex-col min-w-0">
                      <span className="font-medium truncate">{lead.name}</span>
                      <span className="text-xs text-muted-foreground truncate">
                        {lead.phone || lead.email || "—"}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
          {selectedLead && (
            <p className="text-xs text-muted-foreground">
              Selecionado: <span className="font-medium text-foreground">{selectedLead.name}</span>
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
            Cancelar
          </Button>
          <Button
            onClick={() => void handleForward()}
            disabled={sending || !selectedLead || !sourceIntegrationId}
          >
            {sending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Send className="mr-2 h-4 w-4" />
            )}
            Encaminhar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
